import React, { useCallback, useEffect, useState } from "react";
import api, { brl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Check, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { pending: "Pendente", paid: "Pago", overdue: "Atrasado" };
const STATUS_COLOR = { pending: "bg-amber-500", paid: "bg-emerald-600", overdue: "bg-rose-600" };

export default function AccountsBase({ kind }) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ kind, description: "", party: "", amount: 0, due_date: "", category: "" });

  const load = useCallback(() => api.get(`/accounts?kind=${kind}`).then((r) => setItems(r.data)), [kind]);
  useEffect(() => { load(); setForm((f) => ({ ...f, kind })); }, [load, kind]);

  const save = async () => {
    if (!form.description || !form.amount || !form.due_date) return toast.error("Preencha os campos obrigatórios");
    try {
      await api.post("/accounts", { ...form, amount: Number(form.amount) });
      setOpen(false);
      setForm({ kind, description: "", party: "", amount: 0, due_date: "", category: "" });
      toast.success("Conta criada");
      // Defer refetch until Radix Dialog has fully unmounted (prevents removeChild race)
      setTimeout(load, 350);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const pay = async (id) => {
    await api.patch(`/accounts/${id}/pay`);
    toast.success(kind === "payable" ? "Marcado como pago" : "Marcado como recebido");
    load();
  };

  const remove = async (id) => {
    if (!confirm("Excluir?")) return;
    await api.delete(`/accounts/${id}`);
    load();
  };

  const filtered = status === "all" ? items : items.filter((i) => i.status === status);
  const totalPending = items.filter((i) => i.status !== "paid").reduce((s, i) => s + i.amount, 0);
  const totalPaid = items.filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0);

  const title = kind === "payable" ? "Contas a Pagar" : "Contas a Receber";
  const partyLabel = kind === "payable" ? "Fornecedor" : "Cliente";

  return (
    <div className="space-y-6" data-testid={`accounts-${kind}-page`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Financeiro</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">{title}</h1>
        </div>
        <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid={`new-account-${kind}-btn`}>
          <Plus className="w-4 h-4 mr-2" /> Nova conta
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">A {kind === "payable" ? "pagar" : "receber"}</div>
          <div className="kpi-num text-3xl mt-2 text-rose-600">{brl(totalPending)}</div>
        </CardContent></Card>
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">{kind === "payable" ? "Pago" : "Recebido"}</div>
          <div className="kpi-num text-3xl mt-2 text-emerald-600">{brl(totalPaid)}</div>
        </CardContent></Card>
        <Card className="card-outline"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500">Total</div>
          <div className="kpi-num text-3xl mt-2">{items.length}</div>
        </CardContent></Card>
      </div>

      <div className="flex gap-2">
        {["all", "pending", "overdue", "paid"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`filter-${s}`}
            className={"px-3 py-1.5 rounded-full text-xs font-medium border " +
              (status === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600")}>
            {s === "all" ? "Todos" : STATUS_LABEL[s]}
          </button>
        ))}
      </div>

      <Card className="card-outline">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Descrição</TableHead><TableHead>{partyLabel}</TableHead>
              <TableHead>Categoria</TableHead><TableHead>Vencimento</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead>Status</TableHead><TableHead className="text-right">Ações</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((a) => (
                <TableRow key={a.id} data-testid={`account-row-${a.id}`}>
                  <TableCell className="font-medium">{a.description}</TableCell>
                  <TableCell>{a.party || "—"}</TableCell>
                  <TableCell>{a.category || "—"}</TableCell>
                  <TableCell className="kpi-num">{a.due_date}</TableCell>
                  <TableCell className="text-right kpi-num">{brl(a.amount)}</TableCell>
                  <TableCell><Badge className={STATUS_COLOR[a.status]}>{STATUS_LABEL[a.status]}</Badge></TableCell>
                  <TableCell className="text-right">
                    {a.status !== "paid" && (
                      <Button size="icon" variant="ghost" className="text-emerald-600" onClick={() => pay(a.id)} data-testid={`pay-${a.id}`}>
                        <Check className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="text-rose-600" onClick={() => remove(a.id)} data-testid={`del-acc-${a.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">Nenhuma conta.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="account-dialog">
          <DialogHeader><DialogTitle className="font-display">Nova conta a {kind === "payable" ? "pagar" : "receber"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Descrição *</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="acc-desc" /></div>
            <div><Label>{partyLabel}</Label>
              <Input value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} data-testid="acc-party" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Valor *</Label>
                <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="acc-amount" /></div>
              <div><Label>Vencimento *</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="acc-due" /></div>
            </div>
            <div><Label>Categoria</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Aluguel, energia, salário..." data-testid="acc-cat" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-account-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
